import React, { useEffect, useState } from 'react'
import { Modal, Button, TreeSelect, message } from 'antd'
import { PlusOutlined, CloseOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { getPrioritizedSource, getPrioritizedSourceReset, updateSourcePriority, updateSourcePriorityReset } from '../../../store/slices/manage-playbook/prioritizeSource'
import { getParentAndChildrenOptions } from '../../../helpers/array-format'

const convertBackDefaultSources = (arr) => {
    return arr.reduce((acc, { sourceType, subSourceType }) => {
        const validSourceType = Array.isArray(sourceType) ? sourceType : [];
        const validSubSourceType = Array.isArray(subSourceType) ? subSourceType : [];
        acc.push(...validSourceType, ...validSubSourceType);
        return acc;
    }, []);
}

const convertBackPrioritySources = (arr) => {
    return arr?.map(({ sourceType, subSourceType }) => {
        const validSourceType = Array.isArray(sourceType) ? sourceType : [];
        const validSubSourceType = Array.isArray(subSourceType) ? subSourceType : [];
        return [[...validSourceType, ...validSubSourceType]];
    });
}

const filterTreeData = (data, ids) => {
    return data
        .map(node => {
            const isSelected = ids?.includes(node?.value);
            if (node?.children?.length) {
                const filteredChildren = filterTreeData(node?.children, ids);

                if (isSelected) {
                    const disableAllChildren = (childNode) => ({
                        ...childNode,
                        disableCheckbox: true,
                        checked: true,
                        children: childNode.children ? childNode.children.map(disableAllChildren) : []
                    });
                    return { ...node, children: node?.children?.map(disableAllChildren), disableCheckbox: true, checked: true };
                }
                return { ...node, children: filteredChildren, disableCheckbox: false };
            }
            return isSelected ? { ...node, disableCheckbox: true, checked: true } : { ...node, disableCheckbox: false, checked: false };
        })
        .filter(node => node);
};

const processPriorityPayload = (node, selectedPriority) => {
    const result = selectedPriority?.filter(priority => Array.isArray(priority?.[0]) && priority[0]?.length > 0)?.map((priority, index) => {
        const { parents, children } = getParentAndChildrenOptions({ data: node, selectedSource: priority[0] });
        const formattedObject = {
            sourceType: [...parents],
            subSourceType: [...children],
            priority: index + 1
        }
        return formattedObject;
    })

    return result;
}

const PrioritizeSource = ({ open, onClose, sourceOptions, selectedQuestion, selectedRows, selectedRowKeys }) => {
    const [prioritySource, setPrioritySource] = useState([[''], ['']])
    const [defaultSource, setDefaultSource] = useState(["all"])
    const [isExpanded, setIsExpanded] = useState(false)
    const [options, setOptions] = useState([]);


    const dispatch = useDispatch()

    const { data: prioritizedSourceData, isLoading: getPriorityLoading, error: getPrioritizedSourceFailure } = useSelector((state) => state.getPrioritizedSource)
    const { isLoading: updateSourcePriorityLoading, success: updateSourcePrioritySuccess, error: updateSourcePriorityFailure, data: updateSourcePriorityData } = useSelector((state) => state.updateSourcePriority)

    const handleAddItem = () => {
        setPrioritySource([...prioritySource, ''])
    }

    const handleChange = (index, value) => {
        const newItems = [...prioritySource]
        newItems[index] = [value]
        setPrioritySource(newItems);

        const arr = filterTreeData(options, newItems?.flat(newItems?.length));
        setOptions([...arr]);
    }

    const deletePriority = (index) => {
        const newItems = [...prioritySource]
        newItems.splice(index, 1)
        setPrioritySource(newItems)
    }

    const onChangeDefaultSource = (val) => {
        if (val?.at(-1) === "all") {
            setDefaultSource(['all']);
        } else {
            if (val?.includes('all')) {
                val?.splice(val?.indexOf('all'), 1);
            }
            setDefaultSource([...val]);
        }
    }

    const savePriority = () => {
        if (!defaultSource?.length) {
            message.error('Please select default disclosure to proceed');
            return;
        }

        const playbookQuestionIds = selectedQuestion ? [selectedQuestion?.id] : selectedRowKeys;
        const sourcePriorityPayload = {
            priority: processPriorityPayload(sourceOptions, prioritySource),
            default: []
        };

        // If 'all' is selected, no default sources are required
        if (!defaultSource?.includes('all')) {
            const { parents, children } = getParentAndChildrenOptions({
                data: sourceOptions,
                selectedSource: defaultSource,
            });
            sourcePriorityPayload.default.push({ sourceType: parents, subSourceType: children });
        }

        const payload = {
            playbookQuestionId: playbookQuestionIds,
            sourcePriority: sourcePriorityPayload
        };

        dispatch(getPrioritizedSourceReset());
        dispatch(updateSourcePriority(payload));
    }

    const onCloseModal = (getQuestions) => {
        setPrioritySource([[''], ['']])
        setDefaultSource(["all"])
        onClose(getQuestions)
    }

    useEffect(() => {
        if (open && selectedQuestion?.id) {
            if (selectedQuestion?.id) {
                dispatch(getPrioritizedSource(selectedQuestion?.id))
            } else message.error('Playbook id not found')
        }
    }, [open])

    useEffect(() => {
        const priority = prioritizedSourceData?.sourcePriority?.priority
        const defaultSource = prioritizedSourceData?.sourcePriority?.default
        if (priority?.length) {
            setPrioritySource(convertBackPrioritySources(priority));
            const selectedPriorityIds = convertBackDefaultSources(priority);
            setOptions(filterTreeData(options, selectedPriorityIds));
        }
        if (defaultSource?.length > 0) {
            const arr = convertBackDefaultSources(defaultSource)
            setDefaultSource(arr?.length > 0 ? [...arr] : ['all'])
        }
    }, [prioritizedSourceData])

    useEffect(() => {
        if (updateSourcePrioritySuccess) {
            message.success(updateSourcePriorityData?.message)
            onCloseModal(true)
        }
        if (updateSourcePriorityFailure) {
            message.error(updateSourcePriorityFailure?.message)
        }
        dispatch(updateSourcePriorityReset())
    }, [updateSourcePrioritySuccess, updateSourcePriorityFailure])

    useEffect(() => {
        if (sourceOptions?.length > 0) {
            setOptions([...sourceOptions]);
        }
    }, [sourceOptions])

    return (
        <Modal className="dynamic-select-modal" open={open} onCancel={() => onCloseModal()} footer={null} loading={getPriorityLoading || updateSourcePriorityLoading}>
            <div className="dynamic-select-container">
                <div className='details-section'>
                    <div className='details-content'>
                        <div className='dp-code-container'>
                            <div className='input-label'>Dp Code:</div>
                            {selectedQuestion ? (
                                <div>{selectedQuestion?.dpCode}</div>
                            ) : (
                                <div>
                                    {selectedRows?.slice(0, 3)?.map(item => item?.dpCode)?.join(', ')}
                                    {selectedRows.length > 3 && (
                                        <span> ... +{selectedRows.length - 3} more </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {selectedQuestion && (
                            <div className='question-container'>
                                <div className='input-label'>Question:</div>
                                <div>
                                    {isExpanded ? selectedQuestion?.criteria : selectedQuestion?.criteria?.slice(0, 100)}
                                    {selectedQuestion?.criteria?.length > 100 && (
                                        <span onClick={() => setIsExpanded(!isExpanded)} className='read-more-button'>{isExpanded ? '...Read Less' : '...Read More'}</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="page-title">Prioritize Source</div>
                {prioritySource.map((value, index) => (
                    <div className="dynamic-select-parent">
                        <span className="dynamic-select-index">{index + 1}</span>
                        <TreeSelect
                            key={index}
                            className="source-select dynamic-select"
                            popupClassName="filters-select"
                            value={value?.[0] || undefined}
                            onChange={(val) => handleChange(index, val)}
                            treeData={options}
                            placeholder="Select"
                            allowClear
                            showSearch
                            multiple
                            showCheckedStrategy="SHOW_PARENT"
                            treeCheckable
                            filterTreeNode={(input, option) => {
                                return (
                                    option?.key?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0 ||
                                    option?.title?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0
                                );
                            }}
                        />
                        <Button
                            disabled={index < 2}
                            type="text"
                            style={index < 2 ? { opacity: 0, cursor: 'default', margin: 0 } : { margin: 0 }}
                            icon={<CloseOutlined />}
                            onClick={() => deletePriority(index)}
                        />
                    </div>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={handleAddItem}>
                    Add Item
                </Button>
                <div className="input-label">
                    Default Disclosure
                    <span>*</span>
                </div>
                <div className="dynamic-select-parent">
                    <TreeSelect
                        className="source-select dynamic-select"
                        popupClassName="filters-select"
                        style={{ width: '100%' }}
                        value={defaultSource}
                        onChange={(val) => onChangeDefaultSource(val)}
                        treeData={[{ title: "All", value: "all" }, ...sourceOptions]}
                        placeholder="Select"
                        allowClear
                        showSearch
                        multiple
                        showCheckedStrategy="SHOW_PARENT"
                        treeCheckable
                        filterTreeNode={(input, option) => {
                            return (
                                option?.key?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0 ||
                                option?.title?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0
                            );
                        }}
                    />
                </div>
                <div className="action-buttons-container">
                    <div className="action-button" onClick={savePriority}>
                        Save Priority
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default PrioritizeSource
